import { create } from 'zustand';
import type {
  DocumentDto,
  DocumentFacetCountsDto,
  DocumentListResponseDto,
  DocumentMachineFacetOptionDto,
} from '@/types/document';

export type BrowsableDocument = DocumentDto;
export type DocumentGroupingMode = 'none' | 'machine' | 'documentType' | 'status';
export type DocumentSortMode = 'createdAtDesc' | 'createdAtAsc' | 'fileNameAsc' | 'fileNameDesc';

interface DocumentBrowserFilters {
  search: string;
  status: string | null;
  sourceKind: string | null;
  documentType: string | null;
  language: string | null;
  machineModelId: string | null;
}

interface DocumentBrowserState {
  documents: BrowsableDocument[];
  availableDocumentTypes: string[];
  availableLanguages: string[];
  availableMachines: DocumentMachineFacetOptionDto[];
  facetCounts: DocumentFacetCountsDto;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  filters: DocumentBrowserFilters;
  viewMode: 'table' | 'cards' | 'timeline';
  groupBy: DocumentGroupingMode;
  sortBy: DocumentSortMode;

  loadDocuments: (workspaceId: string, token: string | null) => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: Partial<DocumentBrowserFilters>) => void;
  setViewMode: (mode: 'table' | 'cards' | 'timeline') => void;
  setGroupBy: (mode: DocumentGroupingMode) => void;
  setSortBy: (mode: DocumentSortMode) => void;
  resetFilters: () => void;
}

const DEFAULT_FILTERS: DocumentBrowserFilters = {
  search: '',
  status: null,
  sourceKind: null,
  documentType: null,
  language: null,
  machineModelId: null,
};

export const useDocumentBrowserStore = create<DocumentBrowserState>()((set, get) => ({
  documents: [],
  availableDocumentTypes: [],
  availableLanguages: [],
  availableMachines: [],
  facetCounts: {
    statuses: {},
    sourceKinds: {},
    documentTypes: {},
    languages: {},
    machines: {},
  },
  totalCount: 0,
  page: 1,
  pageSize: 50,
  totalPages: 0,
  loading: false,
  error: null,
  filters: { ...DEFAULT_FILTERS },
  viewMode: 'table',
  groupBy: 'none',
  sortBy: 'createdAtDesc',

  loadDocuments: async (workspaceId, token) => {
    set({ loading: true, error: null });
    try {
      const { page, pageSize, filters } = get();
      const params = new URLSearchParams({
        workspaceId,
        page: String(page),
        pageSize: String(pageSize),
      });
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.sourceKind) params.set('sourceKind', filters.sourceKind);
      if (filters.documentType) params.set('documentType', filters.documentType);
      if (filters.language) params.set('language', filters.language);
      if (filters.machineModelId) params.set('machineModelId', filters.machineModelId);

      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`/api/dotnet/documents?${params}`, { headers });
      if (!response.ok) {
        throw new Error(`Fehler beim Laden der Dokumente (${response.status})`);
      }

      const data = (await response.json()) as DocumentListResponseDto | DocumentDto[];
      const isListResponse = !Array.isArray(data);
      const items = isListResponse ? (data.items ?? []) : data;
      const availableDocumentTypes = isListResponse
        ? data.availableDocumentTypes
        : Array.from(new Set(items.map((item: DocumentDto) => item.documentType).filter((value): value is string => !!value))).sort();
      const availableLanguages = isListResponse
        ? data.availableLanguages
        : Array.from(new Set(items.map((item: DocumentDto) => item.language).filter((value): value is string => !!value))).sort();
      const availableMachines = isListResponse
        ? data.availableMachines
        : buildAvailableMachines(items);
      const facetCounts = Array.isArray(data)
        ? {
            statuses: buildFacetCounts(items.map((item) => item.status)),
            sourceKinds: buildSourceFacetCounts(items),
            documentTypes: buildFacetCounts(items.map((item) => item.documentType).filter((value): value is string => !!value)),
            languages: buildFacetCounts(items.map((item) => item.language).filter((value): value is string => !!value)),
            machines: buildFacetCounts(items.map((item) => item.machineModelId).filter((value): value is string => !!value)),
          }
        : (data.facetCounts ?? {
            statuses: {},
            sourceKinds: {},
            documentTypes: {},
            languages: {},
            machines: {},
          });

      set({
        documents: items,
        availableDocumentTypes,
        availableLanguages,
        availableMachines,
        facetCounts,
        totalCount: isListResponse ? (data.totalCount ?? items.length) : items.length,
        page: isListResponse ? (data.page ?? 1) : 1,
        pageSize: isListResponse ? (data.pageSize ?? pageSize) : items.length,
        totalPages: isListResponse ? (data.totalPages ?? 1) : 1,
        loading: false,
      });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  },

  setPage: (page) => set({ page }),

  setFilters: (partial) =>
    set((state) => ({
      filters: { ...state.filters, ...partial },
      page: 1,
    })),

  setViewMode: (mode) => set({ viewMode: mode }),

  setGroupBy: (mode) => set({ groupBy: mode }),

  setSortBy: (mode) => set({ sortBy: mode }),

  resetFilters: () => set({ filters: { ...DEFAULT_FILTERS }, page: 1 }),
}));

function buildFacetCounts(values: string[]): Record<string, number> {
  return values.reduce<Record<string, number>>((acc, value) => {
    acc[value] = (acc[value] ?? 0) + 1;
    return acc;
  }, {});
}

function buildSourceFacetCounts(items: DocumentDto[]): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    const sourceKind = item.sourceKind ?? 'upload';
    acc[sourceKind] = (acc[sourceKind] ?? 0) + 1;
    if (sourceKind !== 'upload') {
      acc.connector = (acc.connector ?? 0) + 1;
    }
    return acc;
  }, {});
}

function buildAvailableMachines(items: DocumentDto[]): DocumentMachineFacetOptionDto[] {
  const seen = new Map<string, string>();
  for (const item of items) {
    if (!item.machineModelId) {
      continue;
    }

    if (!seen.has(item.machineModelId)) {
      seen.set(item.machineModelId, item.machineLabel ?? item.machineModelId);
    }
  }

  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, 'de'));
}
